import React, { useState } from 'react';
import {
  BodyText,
  BoldBodyText,
  Button,
  FlexRow,
  Grid,
  IconType,
  RotatingIcon,
  Tooltip,
  Header3Text,
  DateField,
  NumberField,
} from 'ui-toolkit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { Form, Formik } from 'formik';
import styled from 'styled-components';
import { reissueDiscountValidation } from '../../../services/Validation/reissueDiscountValidation';
import NoPermissionsErrorMessage from '../../shared/permissions/NoPermissionsErrorMessage';
import { StyledFeeSection, StyledFeeSectionHeader } from '../fees/FeeSections.Styled';
import { IReissueDiscountData } from '../../../ui-toolkit/discounts/types/IReissueDiscount';

interface IReissueDiscountsProps {
  reissueDiscount?: IReissueDiscountData;
  onSubmitDiscount: (data: IReissueDiscountData) => void;
  isSubmitting: boolean;
  hasEditPermissions?: boolean;
}

export const ReissueDiscounts: React.FC<IReissueDiscountsProps> = ({
  reissueDiscount,
  isSubmitting,
  onSubmitDiscount,
  hasEditPermissions,
}) => {
  const [showInfo, setShowInfo] = useState<boolean>(false);
  return (
    <StyledFeeSection>
      <StyledFeeSectionHeader>
        <Formik
          initialValues={{
            priorLoanDate: null,
            payoffBalance: null,
            priorLoanAmount: null,
            applied: false,
            ...reissueDiscount,
          }}
          validationSchema={reissueDiscountValidation}
          onSubmit={(values) => {
            onSubmitDiscount(values as IReissueDiscountData);
          }}
        >
          {({ values, handleSubmit, isValid, setFieldValue }) => (
            <Form onSubmit={handleSubmit}>
              <Grid>
                <FlexRow>
                  <Header3Text>
                    Discounts &nbsp;
                    <NoPermissionsErrorMessage hasPermissions={hasEditPermissions} />
                  </Header3Text>
                  <Tooltip
                    tooltipShown={showInfo}
                    tooltipContent={
                      <TooltipContent>
                        <BoldBodyText>Discounts Automatically Applied</BoldBodyText>
                        <BodyText>
                          Dates and amounts shown are based on available information but can be overridden.
                        </BodyText>
                      </TooltipContent>
                    }
                  >
                    <Button intent={'ghost'} onClick={() => setShowInfo(!showInfo)}>
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </Button>
                  </Tooltip>
                </FlexRow>
                <Grid templateColumns="360px 360px" alignItems={'center'}>
                  <DateField label={'Prior Policy Date'} readOnly={!hasEditPermissions} name={'priorLoanDate'} />
                  <div>
                    <Button
                      intent={'ghost'}
                      type="button"
                      onClick={() => setFieldValue('priorLoanDate', null)}
                      disabled={!hasEditPermissions}
                    >
                      Clear
                    </Button>
                  </div>
                  <Grid columnCount={2}>
                    <NumberField
                      label={'Full Previous Loan Amount'}
                      name={'priorLoanAmount'}
                      mode={'currency'}
                      placeholder={'00.00'}
                      valueIsCents
                      readOnly={!hasEditPermissions}
                    />
                    <NumberField
                      label={'Written Payoff Balance'}
                      name={'payoffBalance'}
                      mode={'currency'}
                      placeholder={'00.00'}
                      valueIsCents
                      readOnly={!hasEditPermissions}
                    />
                  </Grid>
                  <div>
                    <Button
                      intent={'ghost'}
                      type="button"
                      onClick={() => {
                        setFieldValue('payoffBalance', null);
                        setFieldValue('priorLoanAmount', null);
                      }}
                      disabled={!hasEditPermissions}
                    >
                      Clear
                    </Button>
                  </div>
                  <Grid templateColumns={'453px'}>
                    <FlexRow justifyContent={'flex-end'}>
                      {isSubmitting && <RotatingIcon type={IconType.Loading} />}
                      <Button
                        intent={'secondary'}
                        type="submit"
                        disabled={(!isValid && isSubmitting) || !hasEditPermissions}
                      >
                        Recalculate Title Fees
                      </Button>
                    </FlexRow>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </StyledFeeSectionHeader>
    </StyledFeeSection>
  );
};

const TooltipContent = styled.div`
  width: 300px;
`;
